/**
 * SSOT for the Serial / IMEI / Warranty-until lines of a serial-tracked receipt item (T-484 / T-491).
 *
 * The on-screen receipt (and its print / reprint, which print the same view) and the ESC/POS thermal
 * receipt both read the item from `GET /sales/:id/receipt` and format its serial block through this one
 * helper, so they cannot disagree on which lines appear or what they say.
 */
export interface ReceiptSerialInfo {
  serialNo: string;
  imei1?: string | null;
  imei2?: string | null;
  warrantyExpiry?: string | null;
}

export interface ReceiptSerialLabels {
  serialNo: string;
  imei: string;
  warrantyUntil: string;
}

export interface ReceiptSerialLine {
  key: 'serial' | 'imei' | 'warranty';
  label: string;
  value: string;
  /** Identifiers render in a monospace face; the warranty date does not. */
  mono: boolean;
}

export function serialReceiptLines(
  serial: ReceiptSerialInfo | null | undefined,
  labels: ReceiptSerialLabels,
  formatDateFn: (isoDate: string) => string,
): ReceiptSerialLine[] {
  if (!serial) return [];
  const lines: ReceiptSerialLine[] = [
    { key: 'serial', label: labels.serialNo, value: serial.serialNo, mono: true },
  ];
  if (serial.imei1) {
    lines.push({
      key: 'imei',
      label: labels.imei,
      value: serial.imei2 ? `${serial.imei1} / ${serial.imei2}` : serial.imei1,
      mono: true,
    });
  }
  if (serial.warrantyExpiry) {
    lines.push({
      key: 'warranty',
      label: labels.warrantyUntil,
      value: formatDateFn(serial.warrantyExpiry),
      mono: false,
    });
  }
  return lines;
}
